import { useLazyQuery } from "@apollo/client";
import { IonButton, IonCol, IonGrid, IonRow, IonText } from "@ionic/react";
import { addDays } from "date-fns";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FormattedMessage, useIntl } from "react-intl";
import { BooleanParam, useQueryParams } from "use-query-params";

import SecondaryButton from "@components/buttons/SecondaryButton";
import Toggle from "@components/buttons/Toggle";
import ExpertDashboardActivities from "@components/expert/ExpertDashboardActivities";
import ExpertDashboardConsults from "@components/expert/ExpertDashboardConsults";
import ExpertDashboardOverview from "@components/expert/ExpertDashboardOverview";
import styles from "@components/expert/ExpertDashboardPage.module.css";
import ExpertDashboardTasks from "@components/expert/ExpertDashboardTasks";
import ExpertKPIs from "@components/expert/ExpertKPIs";
import ExpertMessageGroup from "@components/expert/ExpertMessageGroup";
import ExpertsFilters from "@components/forms/ExpertsFilters";
import HandleQuery from "@components/HandleQuery";
import AppPage from "@components/pageTypes/AppPage";
import HugeText from "@components/text/HugeText";
import LargeText from "@components/text/LargeText";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import { threeDaysInMS } from "@typing/Constants";
import {
  ExpertGlobalRoleEnum,
  GetExpertConsultsDocument,
  GetExpertConsultsQuery,
  GetExpertConsultsQueryVariables,
  GetExpertUnreadMessagesDocument,
  GetExpertUnreadMessagesQuery,
  GetExpertUnreadMessagesQueryVariables
} from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";
import { sortByTimestamp } from "@utils/modelUtils";
import { dateStringFromDate } from "@utils/timeUtils";

type Filters = ComponentProps<typeof ExpertsFilters>["filters"];

type MessageType = NonNullable<
  NonNullable<NonNullable<GetExpertUnreadMessagesQuery["expertUnreadMessages"]["edges"]>[number]>["node"]
>;

const ExpertDashboardPage = () => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const [query, setQuery] = useQueryParams({
    managerView: { ...BooleanParam, urlName: "expertDashManagerView" }
  });

  const [filters, setFilters] = useState<Filters>({
    expertId: myExpert?.id ?? ""
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const managerViewOpener = useOpener(query.managerView ?? false);
  const messagesRef = useRef<HTMLDivElement>(null);

  const activityStartTime = useMemo(() => {
    const defaultDate = new Date();
    return new Date(defaultDate.getTime() - threeDaysInMS);
  }, []);

  const startDate = useMemo(() => dateStringFromDate(new Date(), myExpert?.timezone), [myExpert?.timezone]);
  const endDate = useMemo(() => dateStringFromDate(addDays(new Date(), 3), myExpert?.timezone), [myExpert?.timezone]);

  const [getExpertConsults, getExpertConsultsQuery] = useLazyQuery<
    GetExpertConsultsQuery,
    GetExpertConsultsQueryVariables
  >(GetExpertConsultsDocument);

  useEffect(() => {
    if (myExpert) {
      getExpertConsults({
        variables: {
          endDate,
          expertId: filters.expertId,
          startDate
        }
      });
    }
  }, [startDate, getExpertConsults, myExpert, endDate, filters.expertId]);

  const [getUnreadMessages, getUnreadMessagesQuery] = useLazyQuery<
    GetExpertUnreadMessagesQuery,
    GetExpertUnreadMessagesQueryVariables
  >(GetExpertUnreadMessagesDocument);

  useEffect(() => {
    getUnreadMessages({
      variables: {
        expertId: filters.expertId
      }
    });
  }, [filters.expertId, getUnreadMessages]);

  useSubscribeToJourneys(() => {
    getExpertConsultsQuery.refetch();
    getUnreadMessagesQuery.refetch();
  });

  useEffect(() => {
    setQuery({
      managerView: managerViewOpener.isOpen
    });
  }, [managerViewOpener.isOpen, setQuery]);

  useEffect(() => {
    if (!getUnreadMessagesQuery.loading && getUnreadMessagesQuery.data?.expertUnreadMessages.pageInfo.hasNextPage) {
      getUnreadMessagesQuery.fetchMore({
        variables: { cursor: getUnreadMessagesQuery.data?.expertUnreadMessages.pageInfo.endCursor ?? "" }
      });
    }
    if (!getExpertConsultsQuery.loading && getExpertConsultsQuery.data?.expertConsults.pageInfo.hasNextPage) {
      getExpertConsultsQuery.fetchMore({
        variables: { cursor: getExpertConsultsQuery.data.expertConsults.pageInfo.endCursor }
      });
    }
  }, [getExpertConsultsQuery, getUnreadMessagesQuery]);

  const allMessages = useNodesFromConnection(getUnreadMessagesQuery.data?.expertUnreadMessages);
  const messagesGrouped = useMemo(() => {
    const groups = sortByTimestamp(allMessages, message => message.createdAt)?.reduce(
      (r: { [key: string]: MessageType[] }, a) => {
        r[a.chat.id] = [...(r[a.chat.id] || []), a];
        return r;
      },
      {}
    );
    if (groups) {
      return Object.values(groups);
    }
  }, [allMessages]);

  return (
    <AppPage>
      <Helmet>
        <title>{intl.formatMessage({ id: "pageTitles.expert.dashboard.show" })}</title>
      </Helmet>
      <div className={styles.page}>
        <IonGrid>
          <IonRow>
            <IonGrid className={styles.banner}>
              <IonRow className="ion-align-items-center">
                <IonCol size="6" sizeMd="6" sizeSm="12" sizeXs="12">
                  <div className={styles.bannerContent}>
                    <div>
                      <IonText>
                        <FormattedMessage
                          id="pages.expert.dashboard.welcomeBack"
                          values={{ firstName: myExpert?.firstName }}
                        />
                      </IonText>
                    </div>
                    <div>
                      <IonButton
                        className={styles.headerButton}
                        fill="outline"
                        routerLink="/expert/tasks"
                        size="default"
                      >
                        <FormattedMessage id="pages.expert.dashboard.seeYourTasks" />
                      </IonButton>
                    </div>
                  </div>
                </IonCol>
                <IonCol>
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.managerGroup" })}
                    className={styles.managerGroupImg}
                    src={imageUrl("managerGroup")}
                  />
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonRow>
          <IonRow className="ion-align-items-center">
            <IonCol className="ion-margin-start">
              <LargeText className={styles.headerTitle}>
                <FormattedMessage
                  id={`pages.expert.dashboard.${managerViewOpener.isOpen ? "expertHighlights" : "yourDashboard"}`}
                />
              </LargeText>
            </IonCol>
            <IonCol size="auto">
              <div>
                <Toggle
                  checked={managerViewOpener.isOpen}
                  label={intl.formatMessage({ id: "pages.expert.dashboard.kpi" })}
                  name="managerViewToggle"
                  setValue={managerViewOpener.toggle}
                />
              </div>
            </IonCol>
          </IonRow>

          {!managerViewOpener.isOpen ? (
            <>
              <IonRow>
                <IonCol>
                  {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER && (
                    <div className="ion-margin-bottom">
                      <ExpertsFilters filters={filters} onChange={handleFilterChange} />
                    </div>
                  )}
                  <ExpertDashboardOverview expertId={filters.expertId} messagesRef={messagesRef} />
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="8" sizeMd="8" sizeSm="12" sizeXs="12">
                  <div className={styles.box} ref={messagesRef}>
                    <HandleQuery query={getUnreadMessagesQuery}>
                      {messagesData => (
                        <div className={styles.scroll}>
                          <div className="ion-margin-start">
                            <LargeText className={styles.headerTitle}>
                              <FormattedMessage
                                id="pages.expert.dashboard.boxHeadings.newMessages"
                                values={{ count: messagesData.expertUnreadMessages.totalCount ?? 0 }}
                              />
                            </LargeText>
                          </div>
                          {!messagesGrouped?.length && (
                            <div className={styles.noUnreadMessages}>
                              <img
                                alt={intl.formatMessage({ id: "accessibility.altTags.dancing" })}
                                className={styles.noMessagesImg}
                                src={imageUrl("dancing")}
                              />
                              <HugeText>
                                <FormattedMessage id="pages.expert.dashboard.noUnreadMessages" />
                              </HugeText>
                              <p className="ion-margin-start">
                                <FormattedMessage id="pages.expert.dashboard.youReadThemAll" />
                              </p>
                            </div>
                          )}
                          {messagesGrouped?.map(group => (
                            <div key={group[0].id}>
                              <ExpertMessageGroup
                                group={group}
                                journeyId={group[0].chat.journey.id}
                                refetch={() => getUnreadMessagesQuery.refetch()}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </HandleQuery>
                  </div>
                </IonCol>

                <IonCol size="4" sizeMd="4" sizeSm="12" sizeXs="12">
                  <div className={styles.box}>
                    <HandleQuery query={getExpertConsultsQuery}>
                      {consultsData => (
                        <div className={styles.scroll}>
                          <ExpertDashboardConsults
                            expertConsults={consultsData.expertConsults}
                            expertId={filters.expertId}
                          />
                        </div>
                      )}
                    </HandleQuery>
                  </div>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <div className={styles.box}>
                    <div className={styles.scroll}>
                      <ExpertDashboardTasks expertId={filters.expertId} />
                    </div>
                  </div>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <div className={styles.box}>
                    <div className={styles.scroll}>
                      <div className={styles.titleAndButtonHeader + " ion-margin-bottom"}>
                        <div>
                          <LargeText className={styles.headerTitle}>
                            <FormattedMessage id="pages.expert.dashboard.boxHeadings.keyActivity" />
                          </LargeText>
                        </div>
                        <SecondaryButton
                          routerLink={`/expert/activities?expertId=${filters.expertId}`}
                          textKey="pages.expert.dashboard.viewAllActivity"
                        />
                      </div>
                      <ExpertDashboardActivities
                        expertId={filters.expertId}
                        inDashboard
                        startTime={activityStartTime}
                      />
                    </div>
                  </div>
                </IonCol>
              </IonRow>
            </>
          ) : (
            <div>
              <ExpertKPIs />
            </div>
          )}
        </IonGrid>
      </div>
    </AppPage>
  );
};

export default ExpertDashboardPage;
